import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import styles from './PaymentRecords.module.css';

const EVENT_LABELS = {
  checkout: '购买会员',
  refunded: '已退款',
};

const STATUS_LABELS = {
  success: '成功',
  failed: '失败',
  cancelled: '已取消',
  refunded: '已退款',
};

const PAGE_SIZE = 30;

function PaymentRecords() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        offset: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (filterUserId.trim()) params.append('user_id', filterUserId.trim());
      if (filterStatus) params.append('status', filterStatus);

      const { data } = await api.get(`/payment/admin/records?${params}`);
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('获取支付记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchRecords();
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(isoStr));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // 统计
  const successCount = records.filter((r) => r.status === 'success').length;
  const failedCount = records.filter((r) => r.status === 'failed').length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>支付记录管理</h2>
          <p className={styles.subtitle}>共 {total} 条记录</p>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.filterInput}
          placeholder="按用户 ID 筛选"
          value={filterUserId}
          onChange={(e) => setFilterUserId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          <option value="success">成功</option>
          <option value="failed">失败</option>
          <option value="cancelled">已取消</option>
          <option value="refunded">已退款</option>
        </select>
        <button className={styles.searchBtn} onClick={handleSearch}>
          查询
        </button>
      </div>

      {/* 当页小统计 */}
      <div className={styles.miniStats}>
        <span className={styles.statSuccess}>✓ 成功 {successCount}</span>
        <span className={styles.statFailed}>✗ 失败 {failedCount}</span>
      </div>

      {/* 表格 */}
      {loading ? (
        <p className={styles.hint}>加载中…</p>
      ) : records.length === 0 ? (
        <p className={styles.hint}>暂无记录</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>用户</th>
                <th>套餐</th>
                <th>金额</th>
                <th>事件</th>
                <th>状态</th>
                <th>支付方式</th>
                <th>VIP 到期</th>
                <th>时间</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className={r.status !== 'success' ? styles.rowFailed : ''}>
                  <td>{r.id}</td>
                  <td>
                    <span className={styles.username}>{r.username || '-'}</span>
                    <span className={styles.userId}>#{r.user_id}</span>
                  </td>
                  <td>{r.plan_name}</td>
                  <td>
                    {r.amount && r.amount !== '0'
                      ? `${r.currency === 'CNY' ? '¥' : r.currency || ''}${r.amount}`
                      : '-'}
                  </td>
                  <td>{EVENT_LABELS[r.event_type] || r.event_type}</td>
                  <td>
                    <span className={`${styles.badge} ${styles[`badge_${r.status}`]}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                  <td className={styles.methodCell}>
                    {r.payment_method === 'card' ? '银行卡'
                      : r.payment_method === 'alipay' ? '支付宝'
                      : r.payment_method === 'wechat_pay' ? '微信'
                      : r.payment_method || '-'}
                  </td>
                  <td>{r.vip_expires_at ? formatDate(r.vip_expires_at) : '-'}</td>
                  <td className={styles.timeCell}>{formatDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            上一页
          </button>
          <span>
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}

export default PaymentRecords;

